import React, { useMemo, useState } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import Select from "react-select";
import "./PresenzeDef.css";

// Import hooks
import { useAuthContext } from "../../hooks/useAuthContext";
import { useFirestore } from "../../hooks/useFirestore";
import { useCollection } from "../../hooks/useCollection";
import { useIsAdmin } from "../../hooks/useIsAdmin";

// Stili per i dropdown Select nei modali
const selectModalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 10000 }),
};

function getDateFromValue(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") {
    try {
      return value.toDate();
    } catch {
      return null;
    }
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getTimbraturaReferenceDate(timbratura, sourceType) {
  if (sourceType === "timbrature_qr") {
    if (
      typeof timbratura.businessDate === "string" &&
      timbratura.businessDate.trim()
    ) {
      const parsed = new Date(`${timbratura.businessDate}T00:00:00`);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }
    }

    return getDateFromValue(timbratura.openedAt);
  }

  return getDateFromValue(timbratura.dataRegistrazione);
}

function getTimbraturaReferenceKey(timbratura, sourceType) {
  if (
    sourceType === "timbrature_qr" &&
    typeof timbratura.businessDate === "string" &&
    timbratura.businessDate.trim()
  ) {
    return timbratura.businessDate;
  }

  const referenceDate = getTimbraturaReferenceDate(timbratura, sourceType);
  return referenceDate ? format(referenceDate, "yyyy-MM-dd") : "";
}

function normalizeTimbraturaRecord(timbratura, sourceType) {
  const oraIngresso =
    sourceType === "timbrature_qr"
      ? timbratura.openedAt
      : timbratura.oraIngresso;
  const oraUscita =
    sourceType === "timbrature_qr" ? timbratura.closedAt : timbratura.oraUscita;
  const isAutoClosedBySystem =
    sourceType === "timbrature_qr" &&
    timbratura.autoClosed === true &&
    timbratura.closedBy === "system";

  return {
    ...timbratura,
    sourceType,
    selectionKey: `${sourceType}:${timbratura.id}`,
    sourceLabel:
      sourceType === "timbrature_qr"
        ? isAutoClosedBySystem
          ? "QR (chiusa dal sistema)"
          : "QR"
        : "Classica",
    dataRiferimento: getTimbraturaReferenceDate(timbratura, sourceType),
    dataRiferimentoKey: getTimbraturaReferenceKey(timbratura, sourceType),
    oraIngresso,
    oraUscita,
    canEdit: sourceType === "timbrature",
    canDelete: sourceType === "timbrature",
  };
}

export default function PresenzeDef() {
  const { user } = useAuthContext();
  const admin = useIsAdmin(user);

  // Firestore hooks
  const { documents: presenzeData, error: presenzeError } =
    useCollection("presenze");
  const { documents: timbratureData, error: timbratureError } =
    useCollection("timbrature");
  const { documents: timbratureQrData, error: timbratureQrError } =
    useCollection("timbrature_qr");
  const { documents: usersData } = useCollection("users");
  const { documents: projectsData } = useCollection("projects");
  const { documents: automezziData } = useCollection("automezzi");

  const {
    addDocument: addPresenza,
    updateDocument: updatePresenza,
    deleteDocument: deletePresenza,
  } = useFirestore("presenze");

  const {
    addDocument: addTimbratura,
    updateDocument: updateTimbratura,
    deleteDocument: deleteTimbratura,
  } = useFirestore("timbrature");

  // State management
  const [activeTab, setActiveTab] = useState("presenze");
  const [selectedPresenza, setSelectedPresenza] = useState(null);
  const [selectedTimbratura, setSelectedTimbratura] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("view"); // 'view', 'edit', 'new'
  const [modalType, setModalType] = useState("presenze"); // 'presenze', 'timbrature'

  // Form states for presenze
  const [presenzaForm, setPresenzaForm] = useState({
    userId: "",
    displayName: "",
    dataRegistrazione: format(new Date(), "yyyy-MM-dd"),
    oraIngresso: "",
    oraUscita: "",
    cantiere: "",
    mezzo: "",
    note: "",
    durataPausa: "",
    isFerie: false,
    isMalattia: false,
    codiceMalattia: "",
  });

  // Form states for timbrature
  const [timbraturaForm, setTimbraturaForm] = useState({
    userId: "",
    displayName: "",
    dataRegistrazione: format(new Date(), "yyyy-MM-dd"),
    oraIngresso: "",
    oraUscita: "",
  });

  // Filter states
  const [presenzeFilter, setPresenzeFilter] = useState({
    userId: "",
    dataInizio: "",
    dataFine: "",
    cantiere: "",
  });

  const [timbratureFilter, setTimbratureFilter] = useState({
    userId: "",
    dataInizio: "",
    dataFine: "",
  });

  // Error states
  const [formError, setFormError] = useState(null);

  // Helper function to get user's full name from anagrafica
  const getUserFullName = (userId) => {
    const user = usersData?.find((u) => u.id === userId);
    if (!user) return "Utente non trovato";

    if (user.anagrafica && user.anagrafica.nome && user.anagrafica.cognome) {
      return `${user.anagrafica.nome} ${user.anagrafica.cognome}`;
    }

    return user.displayName || "Nome non disponibile";
  };

  // Prepare options for React-Select dropdowns
  const cantiereOptions =
    projectsData?.map((project) => ({
      value: project.nome,
      label: `${project.categoria} - ${project.nome}`,
    })) || [];

  const mezzoOptions = [
    { value: null, label: "Nessuno" },
    ...(automezziData?.map((automezzo) => ({
      value: automezzo,
      label: `${automezzo.modello} - ${automezzo.targa}`,
    })) || []),
  ];

  const pausaOptions = [
    { value: "", label: "Nessuna pausa" },
    { value: "0.5", label: "0.5 ore (30 min)" },
    { value: "1", label: "1 ora (60 min)" },
    { value: "1.5", label: "1.5 ore (90 min)" },
    { value: "2", label: "2 ore (120 min)" },
  ];

  // Handle React-Select changes
  const handleSelectChange = (selectedOption, actionMeta, formType) => {
    const { name } = actionMeta;
    const value = selectedOption ? selectedOption.value : null;

    if (formType === "presenza") {
      setPresenzaForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else if (formType === "timbratura") {
      setTimbraturaForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const userOptions =
    usersData?.map((u) => ({
      value: u.id,
      label: getUserFullName(u.id),
    })) || [];

  const hasPresenzeFilter =
    presenzeFilter.userId ||
    presenzeFilter.dataInizio ||
    presenzeFilter.dataFine ||
    presenzeFilter.cantiere;

  const hasTimbratureFilter =
    timbratureFilter.userId ||
    timbratureFilter.dataInizio ||
    timbratureFilter.dataFine;

  // Filter data based on current filters
  const filteredPresenze = !hasPresenzeFilter
    ? []
    : presenzeData
        ?.filter((presenza) => {
          if (
            presenzeFilter.userId &&
            presenza.userId !== presenzeFilter.userId
          )
            return false;
          if (
            presenzeFilter.cantiere &&
            !presenza.cantiere?.nome
              ?.toLowerCase()
              .includes(presenzeFilter.cantiere.toLowerCase())
          )
            return false;

          if (presenzeFilter.dataInizio || presenzeFilter.dataFine) {
            const dataPresenza = presenza.dataRegistrazione?.toDate();
            if (dataPresenza) {
              const dataPresenzaStr = format(dataPresenza, "yyyy-MM-dd");
              if (
                presenzeFilter.dataInizio &&
                dataPresenzaStr < presenzeFilter.dataInizio
              )
                return false;
              if (
                presenzeFilter.dataFine &&
                dataPresenzaStr > presenzeFilter.dataFine
              )
                return false;
            }
          }

          return true;
        })
        .sort((a, b) => {
          const dateA = a.dataRegistrazione?.toDate
            ? a.dataRegistrazione.toDate()
            : a.dataRegistrazione
              ? new Date(a.dataRegistrazione)
              : new Date(0);
          const dateB = b.dataRegistrazione?.toDate
            ? b.dataRegistrazione.toDate()
            : b.dataRegistrazione
              ? new Date(b.dataRegistrazione)
              : new Date(0);
          return dateA - dateB;
        }) || [];

  const filteredTimbrature = useMemo(() => {
    if (!hasTimbratureFilter) return [];

    const normalizedTimbrature = [
      ...(timbratureData || []).map((timbratura) =>
        normalizeTimbraturaRecord(timbratura, "timbrature"),
      ),
      ...(timbratureQrData || []).map((timbratura) =>
        normalizeTimbraturaRecord(timbratura, "timbrature_qr"),
      ),
    ];

    return (
      normalizedTimbrature
        ?.filter((timbratura) => {
          if (
            timbratureFilter.userId &&
            timbratura.userId !== timbratureFilter.userId
          )
            return false;
          if (timbratureFilter.dataInizio || timbratureFilter.dataFine) {
            if (timbratura.dataRiferimentoKey) {
              if (
                timbratureFilter.dataInizio &&
                timbratura.dataRiferimentoKey < timbratureFilter.dataInizio
              )
                return false;
              if (
                timbratureFilter.dataFine &&
                timbratura.dataRiferimentoKey > timbratureFilter.dataFine
              )
                return false;
            }
          }

          return true;
        })
        .sort((a, b) => {
          const dateA =
            getDateFromValue(a.oraIngresso) ||
            getDateFromValue(a.dataRiferimento) ||
            new Date(0);
          const dateB =
            getDateFromValue(b.oraIngresso) ||
            getDateFromValue(b.dataRiferimento) ||
            new Date(0);
          return dateA - dateB;
        }) || []
    );
  }, [
    hasTimbratureFilter,
    timbratureData,
    timbratureQrData,
    timbratureFilter.userId,
    timbratureFilter.dataInizio,
    timbratureFilter.dataFine,
  ]);

  // Handle form submissions
  const handleSubmitPresenza = async (e) => {
    e.preventDefault();
    setFormError(null);

    try {
      const selectedUser = usersData?.find((u) => u.id === presenzaForm.userId);
      if (!selectedUser) {
        setFormError("Seleziona un utente valido");
        return;
      }

      const presenzaData = {
        ...presenzaForm,
        displayName: selectedUser.displayName,
        photoURL: selectedUser.photoURL || null,
        dataRegistrazione: new Date(presenzaForm.dataRegistrazione),
        oraIngresso: presenzaForm.oraIngresso
          ? new Date(
              `${presenzaForm.dataRegistrazione}T${presenzaForm.oraIngresso}`,
            )
          : null,
        oraUscita: presenzaForm.oraUscita
          ? new Date(
              `${presenzaForm.dataRegistrazione}T${presenzaForm.oraUscita}`,
            )
          : null,
        cantiere: presenzaForm.cantiere
          ? { nome: presenzaForm.cantiere }
          : null,
        mezzo:
          presenzaForm.mezzo && typeof presenzaForm.mezzo === "object"
            ? {
                id: presenzaForm.mezzo.id,
                modello: presenzaForm.mezzo.modello,
                targa: presenzaForm.mezzo.targa,
              }
            : null,
      };

      if (modalMode === "edit" && selectedPresenza) {
        await updatePresenza(selectedPresenza.id, presenzaData);
      } else {
        await addPresenza(presenzaData);
      }

      setShowModal(false);
      resetPresenzaForm();
    } catch (error) {
      setFormError("Errore durante il salvataggio: " + error.message);
    }
  };

  const handleSubmitTimbratura = async (e) => {
    e.preventDefault();
    setFormError(null);

    try {
      const selectedUser = usersData?.find(
        (u) => u.id === timbraturaForm.userId,
      );
      if (!selectedUser) {
        setFormError("Seleziona un utente valido");
        return;
      }

      const timbraturaData = {
        ...timbraturaForm,
        displayName: selectedUser.displayName,
        photoURL: selectedUser.photoURL || null,
        completato: !!timbraturaForm.oraIngresso && !!timbraturaForm.oraUscita,
        dataRegistrazione: new Date(timbraturaForm.dataRegistrazione),
        oraIngresso: timbraturaForm.oraIngresso
          ? new Date(
              `${timbraturaForm.dataRegistrazione}T${timbraturaForm.oraIngresso}`,
            )
          : null,
        oraUscita: timbraturaForm.oraUscita
          ? new Date(
              `${timbraturaForm.dataRegistrazione}T${timbraturaForm.oraUscita}`,
            )
          : null,
      };

      if (modalMode === "edit" && selectedTimbratura) {
        await updateTimbratura(selectedTimbratura.id, timbraturaData);
      } else {
        await addTimbratura(timbraturaData);
      }

      setShowModal(false);
      resetTimbraturaForm();
    } catch (error) {
      setFormError("Errore durante il salvataggio: " + error.message);
    }
  };

  // Reset form functions
  const resetPresenzaForm = () => {
    setPresenzaForm({
      userId: "",
      displayName: "",
      dataRegistrazione: format(new Date(), "yyyy-MM-dd"),
      oraIngresso: "",
      oraUscita: "",
      cantiere: "",
      mezzo: "",
      note: "",
      durataPausa: "",
      isFerie: false,
      isMalattia: false,
      codiceMalattia: "",
    });
  };

  const resetTimbraturaForm = () => {
    setTimbraturaForm({
      userId: "",
      displayName: "",
      dataRegistrazione: format(new Date(), "yyyy-MM-dd"),
      oraIngresso: "",
      oraUscita: "",
    });
  };

  // Modal handlers
  const openModal = (type, mode, item = null) => {
    setModalType(type);
    setModalMode(mode);
    setFormError(null);

    if (type === "presenze") {
      if (item) {
        setSelectedPresenza(item);
        setPresenzaForm({
          userId: item.userId || "",
          displayName: item.displayName || "",
          dataRegistrazione: item.dataRegistrazione
            ? format(item.dataRegistrazione.toDate(), "yyyy-MM-dd")
            : format(new Date(), "yyyy-MM-dd"),
          oraIngresso: item.oraIngresso
            ? format(item.oraIngresso.toDate(), "HH:mm")
            : "",
          oraUscita: item.oraUscita
            ? format(item.oraUscita.toDate(), "HH:mm")
            : "",
          cantiere: item.cantiere?.nome || "",
          mezzo: item.mezzo || null,
          note: item.note || "",
          durataPausa: item.durataPausa || "",
          isFerie: item.isFerie || false,
          isMalattia: item.isMalattia || false,
          codiceMalattia: item.codiceMalattia || "",
        });
      } else {
        resetPresenzaForm();
      }
    } else {
      if (item) {
        setSelectedTimbratura(item);
        setTimbraturaForm({
          userId: item.userId || "",
          displayName: item.displayName || "",
          dataRegistrazione: item.dataRiferimentoKey
            ? item.dataRiferimentoKey
            : format(new Date(), "yyyy-MM-dd"),
          oraIngresso: getDateFromValue(item.oraIngresso)
            ? format(getDateFromValue(item.oraIngresso), "HH:mm")
            : "",
          oraUscita: getDateFromValue(item.oraUscita)
            ? format(getDateFromValue(item.oraUscita), "HH:mm")
            : "",
        });
      } else {
        setSelectedTimbratura(null);
        resetTimbraturaForm();
      }
    }

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPresenza(null);
    setSelectedTimbratura(null);
    setFormError(null);
  };

  // Delete handlers
  const handleDeletePresenza = async (id) => {
    if (window.confirm("Sei sicuro di voler eliminare questa presenza?")) {
      await deletePresenza(id);
    }
  };

  const handleDeleteTimbratura = async (id) => {
    if (window.confirm("Sei sicuro di voler eliminare questa timbratura?")) {
      await deleteTimbratura(id);
    }
  };

  // Format time helper
  const formatTime = (timestamp) => {
    const date = getDateFromValue(timestamp);
    if (!date) return "-";
    return format(date, "HH:mm", { locale: it });
  };

  // Format date helper
  const formatDate = (timestamp) => {
    const date = getDateFromValue(timestamp);
    if (!date) return "-";
    return format(date, "dd/MM/yyyy", { locale: it });
  };

  if (!admin) {
    return (
      <div className="presenze-def-container">
        <div className="access-denied">
          <h2>Accesso Negato</h2>
          <p>Non hai i permessi necessari per accedere a questa sezione.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="presenze-def-container">
      <div className="presenze-def-header">
        <h1>Gestione Presenze e Timbrature</h1>
        <p>
          Visualizza e gestisci i dati delle collezioni presenze e timbrature
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === "presenze" ? "active" : ""}`}
          onClick={() => setActiveTab("presenze")}
        >
          Presenze ({filteredPresenze.length})
        </button>
        <button
          className={`tab-button ${activeTab === "timbrature" ? "active" : ""}`}
          onClick={() => setActiveTab("timbrature")}
        >
          Timbrature ({filteredTimbrature.length})
        </button>
      </div>

      {/* Presenze Tab */}
      {activeTab === "presenze" && (
        <div className="tab-content">
          <div className="section-header">
            <h2>Presenze</h2>
            <button
              className="btn-add"
              onClick={() => openModal("presenze", "new")}
            >
              + Nuova Presenza
            </button>
          </div>

          {/* Filters */}
          <div className="filters-container">
            <div className="filter-group">
              <label>Utente:</label>
              <Select
                classNamePrefix="react-select"
                className="filter-select"
                styles={selectModalStyles}
                menuPortalTarget={document.body}
                options={userOptions}
                value={
                  userOptions.find(
                    (option) => option.value === presenzeFilter.userId,
                  ) || null
                }
                onChange={(option) =>
                  setPresenzeFilter({
                    ...presenzeFilter,
                    userId: option ? option.value : "",
                  })
                }
                isClearable
                isSearchable
                placeholder="Tutti gli utenti"
              />
            </div>
            <div className="filter-group">
              <label>Data Inizio:</label>
              <input
                type="date"
                value={presenzeFilter.dataInizio}
                onChange={(e) =>
                  setPresenzeFilter({
                    ...presenzeFilter,
                    dataInizio: e.target.value,
                  })
                }
              />
            </div>
            <div className="filter-group">
              <label>Data Fine:</label>
              <input
                type="date"
                value={presenzeFilter.dataFine}
                onChange={(e) =>
                  setPresenzeFilter({
                    ...presenzeFilter,
                    dataFine: e.target.value,
                  })
                }
              />
            </div>
            <div className="filter-group">
              <label>Cantiere:</label>
              <input
                type="text"
                placeholder="Cerca cantiere..."
                value={presenzeFilter.cantiere}
                onChange={(e) =>
                  setPresenzeFilter({
                    ...presenzeFilter,
                    cantiere: e.target.value,
                  })
                }
              />
            </div>
            <button
              className="btn-clear-filters"
              onClick={() =>
                setPresenzeFilter({
                  userId: "",
                  dataInizio: "",
                  dataFine: "",
                  cantiere: "",
                })
              }
            >
              Pulisci Filtri
            </button>
          </div>

          {/* Presenze Table */}
          <div className="table-container">
            {presenzeError && (
              <div className="error-message">
                Errore nel caricamento presenze: {presenzeError}
              </div>
            )}
            <table className="data-table">
              <thead>
                <tr>
                  <th>Utente</th>
                  <th>Data</th>
                  <th>Ingresso</th>
                  <th>Uscita</th>
                  <th>Cantiere</th>
                  <th>Mezzo</th>
                  <th>Tipo</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredPresenze.map((presenza) => (
                  <tr key={presenza.id}>
                    <td>{getUserFullName(presenza.userId)}</td>
                    <td>{formatDate(presenza.dataRegistrazione)}</td>
                    <td>{formatTime(presenza.oraIngresso)}</td>
                    <td>{formatTime(presenza.oraUscita)}</td>
                    <td>{presenza.cantiere?.nome || "-"}</td>
                    <td>
                      {presenza.mezzo?.modello || presenza.mezzo?.targa || "-"}
                    </td>
                    <td>
                      {presenza.isFerie
                        ? "Ferie"
                        : presenza.isMalattia
                          ? "Malattia"
                          : "Lavoro"}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-view"
                          onClick={() =>
                            openModal("presenze", "view", presenza)
                          }
                        >
                          👁️
                        </button>
                        <button
                          className="btn-edit"
                          onClick={() =>
                            openModal("presenze", "edit", presenza)
                          }
                        >
                          ✏️
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDeletePresenza(presenza.id)}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredPresenze.length === 0 && (
              <div className="no-data">Nessuna presenza trovata</div>
            )}
          </div>
        </div>
      )}

      {/* Timbrature Tab */}
      {activeTab === "timbrature" && (
        <div className="tab-content">
          <div className="section-header">
            <h2>Timbrature</h2>
            <button
              className="btn-add"
              onClick={() => openModal("timbrature", "new")}
            >
              + Nuova Timbratura
            </button>
          </div>

          {/* Filters */}
          <div className="filters-container">
            <div className="filter-group">
              <label>Utente:</label>
              <Select
                classNamePrefix="react-select"
                className="filter-select"
                styles={selectModalStyles}
                menuPortalTarget={document.body}
                options={userOptions}
                value={
                  userOptions.find(
                    (option) => option.value === timbratureFilter.userId,
                  ) || null
                }
                onChange={(option) =>
                  setTimbratureFilter({
                    ...timbratureFilter,
                    userId: option ? option.value : "",
                  })
                }
                isClearable
                isSearchable
                placeholder="Tutti gli utenti"
              />
            </div>
            <div className="filter-group">
              <label>Data Inizio:</label>
              <input
                type="date"
                value={timbratureFilter.dataInizio}
                onChange={(e) =>
                  setTimbratureFilter({
                    ...timbratureFilter,
                    dataInizio: e.target.value,
                  })
                }
              />
            </div>
            <div className="filter-group">
              <label>Data Fine:</label>
              <input
                type="date"
                value={timbratureFilter.dataFine}
                onChange={(e) =>
                  setTimbratureFilter({
                    ...timbratureFilter,
                    dataFine: e.target.value,
                  })
                }
              />
            </div>
            <button
              className="btn-clear-filters"
              onClick={() =>
                setTimbratureFilter({
                  userId: "",
                  dataInizio: "",
                  dataFine: "",
                })
              }
            >
              Pulisci Filtri
            </button>
          </div>

          {/* Timbrature Table */}
          <div className="table-container">
            {timbratureError && (
              <div className="error-message">
                Errore nel caricamento timbrature: {timbratureError}
              </div>
            )}
            {timbratureQrError && (
              <div className="error-message">
                Errore nel caricamento timbrature QR: {timbratureQrError}
              </div>
            )}
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Utente</th>
                  <th>Data</th>
                  <th>Ingresso</th>
                  <th>Uscita</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredTimbrature.map((timbratura) => (
                  <tr key={timbratura.selectionKey}>
                    <td>{timbratura.sourceLabel}</td>
                    <td>{getUserFullName(timbratura.userId)}</td>
                    <td>{formatDate(timbratura.dataRiferimento)}</td>
                    <td>{formatTime(timbratura.oraIngresso)}</td>
                    <td>{formatTime(timbratura.oraUscita)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-view"
                          onClick={() =>
                            openModal("timbrature", "view", timbratura)
                          }
                        >
                          👁️
                        </button>
                        <button
                          className="btn-edit"
                          disabled={!timbratura.canEdit}
                          title={
                            timbratura.canEdit
                              ? "Modifica timbratura"
                              : "Le timbrature QR sono in sola lettura"
                          }
                          onClick={() =>
                            timbratura.canEdit &&
                            openModal("timbrature", "edit", timbratura)
                          }
                        >
                          ✏️
                        </button>
                        <button
                          className="btn-delete"
                          disabled={!timbratura.canDelete}
                          title={
                            timbratura.canDelete
                              ? "Elimina timbratura"
                              : "Le timbrature QR non vengono eliminate da questa schermata"
                          }
                          onClick={() =>
                            timbratura.canDelete &&
                            handleDeleteTimbratura(timbratura.id)
                          }
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredTimbrature.length === 0 && (
              <div className="no-data">Nessuna timbratura trovata</div>
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {modalMode === "new"
                  ? "Nuova"
                  : modalMode === "edit"
                    ? "Modifica"
                    : "Visualizza"}{" "}
                {modalType === "presenze" ? "Presenza" : "Timbratura"}
              </h3>
              <button className="btn-close" onClick={closeModal}>
                ×
              </button>
            </div>

            <div className="modal-body">
              {formError && <div className="error-message">{formError}</div>}

              {modalType === "presenze" ? (
                <form onSubmit={handleSubmitPresenza}>
                  <div className="form-group">
                    <label>Utente:</label>
                    <select
                      value={presenzaForm.userId}
                      onChange={(e) =>
                        setPresenzaForm({
                          ...presenzaForm,
                          userId: e.target.value,
                        })
                      }
                      disabled={modalMode === "view"}
                      required
                    >
                      <option value="">Seleziona utente</option>
                      {usersData?.map((user) => (
                        <option key={user.id} value={user.id}>
                          {getUserFullName(user.id)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Data:</label>
                    <input
                      type="date"
                      value={presenzaForm.dataRegistrazione}
                      onChange={(e) =>
                        setPresenzaForm({
                          ...presenzaForm,
                          dataRegistrazione: e.target.value,
                        })
                      }
                      disabled={modalMode === "view"}
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Ora Ingresso:</label>
                      <input
                        type="time"
                        value={presenzaForm.oraIngresso}
                        onChange={(e) =>
                          setPresenzaForm({
                            ...presenzaForm,
                            oraIngresso: e.target.value,
                          })
                        }
                        disabled={modalMode === "view"}
                      />
                    </div>

                    <div className="form-group">
                      <label>Ora Uscita:</label>
                      <input
                        type="time"
                        value={presenzaForm.oraUscita}
                        onChange={(e) =>
                          setPresenzaForm({
                            ...presenzaForm,
                            oraUscita: e.target.value,
                          })
                        }
                        disabled={modalMode === "view"}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Cantiere:</label>
                      <Select
                        name="cantiere"
                        value={cantiereOptions.find(
                          (option) => option.value === presenzaForm.cantiere,
                        )}
                        onChange={(selectedOption, actionMeta) =>
                          handleSelectChange(
                            selectedOption,
                            actionMeta,
                            "presenza",
                          )
                        }
                        options={cantiereOptions}
                        placeholder="Seleziona cantiere"
                        isClearable
                        isDisabled={modalMode === "view"}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        menuPortalTarget={document.body}
                        styles={selectModalStyles}
                      />
                    </div>

                    <div className="form-group">
                      <label>Mezzo:</label>
                      <Select
                        name="mezzo"
                        value={mezzoOptions.find((option) => {
                          const optVal = option.value;
                          const formVal = presenzaForm.mezzo;
                          if (!optVal && !formVal) return true;
                          if (optVal && formVal && optVal.id === formVal.id)
                            return true;
                          return false;
                        })}
                        onChange={(selectedOption, actionMeta) =>
                          handleSelectChange(
                            selectedOption,
                            actionMeta,
                            "presenza",
                          )
                        }
                        options={mezzoOptions}
                        placeholder="Seleziona mezzo"
                        isClearable
                        isDisabled={modalMode === "view"}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        menuPortalTarget={document.body}
                        styles={selectModalStyles}
                      />
                    </div>

                    <div className="form-group">
                      <label>Durata Pausa:</label>
                      <Select
                        name="durataPausa"
                        value={pausaOptions.find(
                          (option) => option.value === presenzaForm.durataPausa,
                        )}
                        onChange={(selectedOption, actionMeta) =>
                          handleSelectChange(
                            selectedOption,
                            actionMeta,
                            "presenza",
                          )
                        }
                        options={pausaOptions}
                        placeholder="Seleziona durata pausa"
                        isClearable
                        isDisabled={modalMode === "view"}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        menuPortalTarget={document.body}
                        styles={selectModalStyles}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Note:</label>
                    <textarea
                      value={presenzaForm.note}
                      onChange={(e) =>
                        setPresenzaForm({
                          ...presenzaForm,
                          note: e.target.value,
                        })
                      }
                      disabled={modalMode === "view"}
                      rows="3"
                      placeholder="Note aggiuntive"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={presenzaForm.isFerie}
                          onChange={(e) =>
                            setPresenzaForm({
                              ...presenzaForm,
                              isFerie: e.target.checked,
                            })
                          }
                          disabled={modalMode === "view"}
                        />
                        Ferie
                      </label>
                    </div>

                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={presenzaForm.isMalattia}
                          onChange={(e) =>
                            setPresenzaForm({
                              ...presenzaForm,
                              isMalattia: e.target.checked,
                            })
                          }
                          disabled={modalMode === "view"}
                        />
                        Malattia
                      </label>
                    </div>
                  </div>

                  {presenzaForm.isMalattia && (
                    <div className="form-group">
                      <label>Codice Malattia:</label>
                      <input
                        type="text"
                        value={presenzaForm.codiceMalattia}
                        onChange={(e) =>
                          setPresenzaForm({
                            ...presenzaForm,
                            codiceMalattia: e.target.value,
                          })
                        }
                        disabled={modalMode === "view"}
                        placeholder="Codice malattia"
                      />
                    </div>
                  )}

                  {modalMode !== "view" && (
                    <div className="modal-actions">
                      <button type="submit" className="btn-save">
                        {modalMode === "edit" ? "Aggiorna" : "Salva"}
                      </button>
                      <button
                        type="button"
                        className="btn-cancel"
                        onClick={closeModal}
                      >
                        Annulla
                      </button>
                    </div>
                  )}
                </form>
              ) : (
                <form onSubmit={handleSubmitTimbratura}>
                  {selectedTimbratura?.sourceType === "timbrature_qr" && (
                    <div className="info-text">
                      Questa timbratura proviene da `timbrature_qr` ed e in sola
                      lettura in questa schermata.
                    </div>
                  )}
                  <div className="form-group">
                    <label>Utente:</label>
                    <select
                      value={timbraturaForm.userId}
                      onChange={(e) =>
                        setTimbraturaForm({
                          ...timbraturaForm,
                          userId: e.target.value,
                        })
                      }
                      disabled={
                        modalMode === "view" ||
                        selectedTimbratura?.sourceType === "timbrature_qr"
                      }
                      required
                    >
                      <option value="">Seleziona utente</option>
                      {usersData?.map((user) => (
                        <option key={user.id} value={user.id}>
                          {getUserFullName(user.id)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Data:</label>
                    <input
                      type="date"
                      value={timbraturaForm.dataRegistrazione}
                      onChange={(e) =>
                        setTimbraturaForm({
                          ...timbraturaForm,
                          dataRegistrazione: e.target.value,
                        })
                      }
                      disabled={
                        modalMode === "view" ||
                        selectedTimbratura?.sourceType === "timbrature_qr"
                      }
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Ora Ingresso:</label>
                      <input
                        type="time"
                        value={timbraturaForm.oraIngresso}
                        onChange={(e) =>
                          setTimbraturaForm({
                            ...timbraturaForm,
                            oraIngresso: e.target.value,
                          })
                        }
                        disabled={
                          modalMode === "view" ||
                          selectedTimbratura?.sourceType === "timbrature_qr"
                        }
                      />
                    </div>

                    <div className="form-group">
                      <label>Ora Uscita:</label>
                      <input
                        type="time"
                        value={timbraturaForm.oraUscita}
                        onChange={(e) =>
                          setTimbraturaForm({
                            ...timbraturaForm,
                            oraUscita: e.target.value,
                          })
                        }
                        disabled={
                          modalMode === "view" ||
                          selectedTimbratura?.sourceType === "timbrature_qr"
                        }
                      />
                    </div>
                  </div>

                  {modalMode !== "view" &&
                    selectedTimbratura?.sourceType !== "timbrature_qr" && (
                      <div className="modal-actions">
                        <button type="submit" className="btn-save">
                          {modalMode === "edit" ? "Aggiorna" : "Salva"}
                        </button>
                        <button
                          type="button"
                          className="btn-cancel"
                          onClick={closeModal}
                        >
                          Annulla
                        </button>
                      </div>
                    )}
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
